import React, { useState, useEffect, useRef } from "react";
import "./StudentsHeader.css";
import "../../hooks/useRipple.css";
import useRipple from "../../hooks/useRipple";
import { useSelector } from "react-redux";
import { getAccountType } from "../../store/auth/selectors";
import {
  checkPermission,
  PERMISSION_TYPES,
} from "../../shared/utils/permissions";
import UploadButton from "../../shared/components/UploadButton/UploadButton";
import useEventsQueries from "../../store/events/eventsQueries";
import Tooltip from "@mui/material/Tooltip";
import formatSchoolAddress from "../../shared/utils/formatSchoolAddress";
import { AnimatePresence, motion } from "motion/react";
import { CONFIG_VARS } from "../../constants/urlConstants/configuration";

const StudentsHeader = ({
  schoolId,
  handleSchoolIdChange,
  handleGetData,
  isLoading,
  activeTab,
  children,
  schoolDetails,
  isUserAuthorized,
  onUploadSuccess,
}) => {
  const buttonRef = useRef(null);
  useRipple(buttonRef);
  const accountType = useSelector(getAccountType);
  const checkUserPermission = (permissionType) =>
    checkPermission(accountType, permissionType);
  const { handleSearchSchools } = useEventsQueries();
  const canUploadToSchool = useSelector(
    (state) => state.uploadPermissions.canUploadToSchool,
  );

  const [searchInput, setSearchInput] = useState("");
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isActiveSearch, setIsActiveSearch] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  const formattedSchoolAddress = schoolDetails
    ? formatSchoolAddress(schoolDetails)
    : "N/A";
  const formattedSchoolId = schoolDetails?.school_id || "N/A";

  const generateDeeplink = (schoolId) => {
    const currentEnv = CONFIG_VARS.currentEnvironment;
    const baseUrl = CONFIG_VARS.deeplinkBaseUrl[currentEnv];

    return `${baseUrl}deeplink/register?school_id=${schoolId}`;
  };

  useEffect(() => {
    if (schoolId) {
      setSearchInput(schoolId.toUpperCase());
    }
  }, [schoolId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".school-search-container")) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (
        searchInput &&
        searchInput.length >= 3 &&
        accountType !== "School Admin"
      ) {
        fetchSchoolOptions(searchInput);
        setShowDropdown(true);
      } else {
        setSchoolOptions([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, accountType]);

  const fetchSchoolOptions = async (searchedSchool) => {
    setIsSearching(true);
    const response = await handleSearchSchools(searchedSchool);
    if (response) {
      const formattedSchools = response.data.map((school) => ({
        label: `${school._source.school_name} - (${school._source.school_id})`,
        value: school._source.school_id,
      }));
      setSchoolOptions(formattedSchools);
    } else {
      setSchoolOptions([]);
    }
    setIsSearching(false);
  };

  const handleSchoolSelect = (school) => {
    setIsActiveSearch(false);
    setSearchInput(school.value);
    handleSchoolIdChange({ target: { value: school.value } });
    setShowDropdown(false);
  };

  const canUploadStudents =
    checkUserPermission(PERMISSION_TYPES.UPLOAD_STUDENTS) && canUploadToSchool;

  const canUploadGlobalOfflineResponse = checkUserPermission(
    PERMISSION_TYPES.UPLOAD_GLOBAL_OFFLINE_RESPONSE,
  );

  useEffect(() => {
    if (!isActiveSearch && searchInput) {
      handleGetData();
    }
  }, [isActiveSearch, searchInput]);

  return (
    <>
      <div className="students-heading">
        <div className="students-heading-content">
          <div>
            <h3>Students</h3>
            <p>Manage your student data</p>
          </div>
          {canUploadGlobalOfflineResponse && (
            <div className="global-upload-button-wrapper">
              <UploadButton
                uploadType="uploadOfflineResponses"
                selectedTab="Global"
              />
            </div>
          )}
        </div>
      </div>

      <div className="school-id-section">
        <span>Search School by ID or Name:</span>
        {accountType === "School Admin" ? (
          <Tooltip title="Restricted for School Admin" followCursor>
            <div className="school-search-container">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  if (accountType !== "School Admin") {
                    setIsActiveSearch(true);
                    setSearchInput(e.target.value.toUpperCase());
                  }
                }}
                placeholder="Please enter a school ID"
                onFocus={() => {
                  if (
                    searchInput &&
                    searchInput.length >= 3 &&
                    accountType !== "School Admin"
                  ) {
                    setShowDropdown(true);
                  }
                }}
                disabled={accountType === "School Admin"}
                style={
                  accountType === "School Admin"
                    ? { cursor: "not-allowed", opacity: 0.6 }
                    : {}
                }
              />
            </div>
          </Tooltip>
        ) : (
          <div className="school-search-container">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => {
                if (accountType !== "School Admin") {
                  setIsActiveSearch(true);
                  setSearchInput(e.target.value.toUpperCase());
                }
              }}
              placeholder="Please enter a school ID"
              onFocus={() => {
                if (
                  searchInput &&
                  searchInput.length >= 3 &&
                  accountType !== "School Admin"
                ) {
                  setShowDropdown(true);
                }
              }}
              disabled={accountType === "School Admin"}
              style={
                accountType === "School Admin"
                  ? { cursor: "not-allowed", opacity: 0.6 }
                  : {}
              }
            />
            {(showDropdown ||
              (searchInput &&
                searchInput.length > 0 &&
                searchInput.length < 3)) &&
              accountType !== "School Admin" && (
                <div className="school-options-dropdown">
                  {searchInput && searchInput.length < 3 ? (
                    <div className="school-dropdown-message">
                      Type at least 3 characters to search
                    </div>
                  ) : isSearching ? (
                    <div className="school-dropdown-message">Loading...</div>
                  ) : schoolOptions.length > 0 ? (
                    schoolOptions.map((school, index) => (
                      <div
                        key={index}
                        className="school-option-item"
                        onClick={() => handleSchoolSelect(school)}
                      >
                        {school.label}
                      </div>
                    ))
                  ) : (
                    <div className="school-dropdown-message">
                      No results found
                    </div>
                  )}
                </div>
              )}
          </div>
        )}
        <button
          ref={buttonRef}
          className="get-data-btn"
          disabled={!searchInput || isLoading || accountType === "School Admin"}
          onClick={() => {
            if (accountType !== "School Admin") {
              handleSchoolIdChange({ target: { value: searchInput } });
              handleGetData();
              setIsActiveSearch(false);
            }
          }}
          style={
            accountType === "School Admin"
              ? { cursor: "not-allowed", opacity: 0.6 }
              : {}
          }
        >
          {isLoading ? "Loading..." : "Get Data"}
        </button>
        {activeTab === "students" &&
          schoolId &&
          canUploadStudents &&
          isUserAuthorized && (
            <div className="upload-button-wrapper">
              <UploadButton
                schoolId={schoolId}
                uploadType="studentRegister"
                selectedTab="Onboarded Students"
                onUploadSuccess={onUploadSuccess}
                schoolDetails={schoolDetails}
              />
            </div>
          )}
      </div>
      {schoolDetails && (
        <div className="school-details-section">
          {`School: ${formattedSchoolAddress} | School ID: ${formattedSchoolId}`}
          <div className="students-copy-button-container">
            {schoolDetails && schoolId && (
              <button
                className="students-copy-registration-link-btn"
                onClick={() => {
                  navigator.clipboard.writeText(generateDeeplink(schoolId));
                  setShowCopyNotification(true);
                  setTimeout(() => {
                    setShowCopyNotification(false);
                  }, 2000);
                }}
              >
                Copy School Registration Link
              </button>
            )}
            <AnimatePresence>
              {showCopyNotification && (
                <motion.div
                  className="students-copy-notification"
                  initial={{ opacity: 0, y: -10, x: "-50%" }}
                  animate={{ opacity: 1, y: 0, x: "-50%" }}
                  exit={{ opacity: 0, y: 10, x: "-50%" }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  Link Copied
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentsHeader;
